import React from "react";
import "./SceduleAuctionListFilter.scss";
import dayjs from "dayjs";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { commonAction } from "../../../../redux/features/commonSlice";
import DownArrowIcon from "../../../../components/icons/DownArrowIcon";
import ResetIcon from "../../../../components/icons/ResetIcon";
import ListFilterButton from "../../buttons/ListFilterButton";

type WeekKey = "1st" | "2nd" | "3rd" | "4th" | "5th";

type FilterType = {
  year: number;
  month: number | null;
  week?: WeekKey;
} | null;

interface Props {
  value: FilterType;
  onChange?(value: FilterType): void;
  rightAction?: React.ReactNode;
  showFiltersBtn?: boolean;
  onClickRefresh?: () => void;
}

const OpenHomeHeaderDateFilter: React.FC<Props> = ({
  value,
  onChange,
  rightAction,
  showFiltersBtn = true,
  onClickRefresh,
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const resetFilter = () => {
    const today = dayjs();
    const currentYear = today.year();
    const currentMonth = today.month();
    const currentDate = today.date();

    const weekNumber = Math.ceil(currentDate / 7);
    const weekMapping: Record<number, WeekKey> = {
      1: "1st",
      2: "2nd",
      3: "3rd",
      4: "4th",
      5: "5th",
    };
    const currentWeek = weekMapping[weekNumber > 5 ? 5 : weekNumber];

    onChange?.({
      year: currentYear,
      month: currentMonth,
      week: currentWeek,
    });

    dispatch(commonAction?.resetFilter());
    const currentParams = new URLSearchParams(window.location.search);
    const subscriptionType = currentParams.get("subscription_type");
    const newParams = new URLSearchParams();
    if (subscriptionType) {
      newParams.set("subscription_type", subscriptionType);
    }
    navigate(`/workspace/auction-list?${newParams.toString()}`);
  };

  const handleYearChange = (newYear: number | null) => {
    if (newYear === null) {
      onChange?.(null);
    } else {
      onChange?.({
        ...(value || { month: null, week: undefined }),
        year: newYear,
      });
    }
  };

  const handleMonthChange = (newMonth: number | null) => {
    if (value?.year) {
      onChange?.({
        ...value,
        month: newMonth,
      });
    }
  };

  const handleWeekChange = (newWeek: WeekKey) => {
    if (value?.year) {
      onChange?.({
        ...value,
        week: newWeek,
      });
    }
  };

  return (
    <div className="auction-date-filter-bar">
      <div className="date-filter-group">
        <div className="select-wrapper">
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <DatePicker
              views={["year"]}
              value={value?.year ? dayjs().year(value.year) : null}
              onChange={(newValue) => {
                handleYearChange(newValue ? newValue.year() : null);
              }}
              slotProps={{
                textField: {
                  size: "small",
                  className: "filter-select-input",
                },
              }}
            />
          </LocalizationProvider>
        </div>

        <div className="select-wrapper">
          <select
            className="filter-select"
            value={value?.month ?? ""}
            disabled={!value?.year}
            onChange={(e) => {
              const m = e.target.value ? Number(e.target.value) : null;
              handleMonthChange(m);
            }}
          >
            <option value="" disabled>
              Month
            </option>
            {Array.from({ length: 12 }, (_, i) => (
              <option key={i} value={i}>
                {dayjs().month(i).format("MMMM")}
              </option>
            ))}
          </select>
          <div className="select-icon">
            <DownArrowIcon />
          </div>
        </div>

        <span className="week-label">Week</span>
        {(["1st", "2nd", "3rd", "4th", "5th"] as WeekKey[]).map((wk) => (
          <button
            key={wk}
            className={`week-pill ${wk === value?.week ? "active" : ""}`}
            disabled={!value?.year}
            onClick={() => {
              handleWeekChange(wk);
            }}
          >
            {wk}
          </button>
        ))}
      </div>

      <div className="action-group">
        {onClickRefresh && (
          <button className="list-top-refresh-btn" onClick={onClickRefresh}>
            <p>Reload</p>
          </button>
        )}

        <button className="reset-btn" type="button" onClick={resetFilter}>
          <ResetIcon />
          Reset&nbsp;Filters
        </button>

        {showFiltersBtn && <ListFilterButton />}
        {rightAction}
      </div>
    </div>
  );
};

export default OpenHomeHeaderDateFilter;
